import { Grid, Autocomplete, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { LocationOn as LocationOnIcon } from "@mui/icons-material";
import { useStation } from "../../../providers/StationProvider";
import mapboxgl from "mapbox-gl";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Location, makeId } from "@emberly/zenith-client";
import { FormatLocation, MapboxQueryToLocation } from "../../../common/maphelpers";

const FORWARD_PASS = (x) => x;
const IS_OPTION_EQUAL = (a, b) => a.mapboxId === b.mapboxId;

export default function MapboxSearch(props) {
  const { label, value, onChange, disabled, freeSolo } = props;
  const { t } = useTranslation();

  const [option, setOption] = useState(value || null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const { station } = useStation();

  const [popupOpen, setPopupOpen] = useState(false);

  const onPopupClose = useCallback(() => setPopupOpen(false), []);
  const sessionTokenRef = useRef("tkn");

  const onPopupOpen = useCallback(() => {
    setPopupOpen(true);
  }, [inputValue]);

  const onFocus = useCallback(() => {
    sessionTokenRef.current = makeId();
  }, []);

  useEffect(() => {
    setOption(value);
  }, [value, station]);

  useEffect(() => {
    let alive = true;
    const timer = setTimeout(() => {

      if (!!inputValue && option?.title !== inputValue && popupOpen) {
        const proximityBias = station?.location?.coordinates?.length === 2 ? `&proximity=${station.location.coordinates.join(",")}` : "";

        // TODO session token and proximity and language, used to be "en"
        axios(`https://api.mapbox.com/search/searchbox/v1/suggest?q=${inputValue}&language=no&country=no%2Cse%2Cdk&session_token=${sessionTokenRef.current}&access_token=${mapboxgl.accessToken}&types=poi,address,street${proximityBias}`)
          .then((res) => {
            const { suggestions } = res.data;
            // todo might need to do something with attribution here, from res.dat
            if (!alive) return;

            // TODO process data
            setOptions(suggestions.filter(t => t.feature_type === "poi" || t.feature_type === "address" || t.feature_type === "street").map((t, key) => ({
              title: t.name,
              address: t.full_address,
              formattedPlace: t.place_formatted,
              mapboxId: t.mapbox_id,
              type: t.feature_type,
              isContact: false,
              key
            })));

          })
          .catch((err) => {
            console.log("err", err);
          });
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      alive = false;
    };
  }, [inputValue, option, station, popupOpen]);


  const onOptionSelected = useCallback((ev, option) => {
    if (!!option) {
      setOption(option);
      // TODO session token

      if (option.isContact) {
        onChange(new Location({ ...option }));
      } else {
        axios(`https://api.mapbox.com/search/searchbox/v1/retrieve/${option.mapboxId}?session_token=${sessionTokenRef.current}&access_token=${mapboxgl.accessToken}`)
          .then(res => {
            const feature = res.data?.features[0];
            onChange(MapboxQueryToLocation(option, feature));
          })
          .catch(err => {
            console.log(err, err);
          });
      }

    } else {
      if (ev?.type !== "change") {
        onChange(new Location({ address: "" }));
      }
    }
  }, [onChange]);


  const onInputChange = useCallback((event, newInputValue) => {
    setInputValue(newInputValue);

    if (freeSolo && event?.type === "change") {

      onChange(new Location({
        address: newInputValue,
      }));

    }
  }, [onChange, freeSolo]);

  return (
    (<Autocomplete
      onReset={console.log}
      getOptionLabel={FormatLocation}
      isOptionEqualToValue={IS_OPTION_EQUAL}
      options={options}
      onOpen={onPopupOpen}
      onClose={onPopupClose}
      autoComplete
      fullWidth
      freeSolo
      clearOnBlur
      filterOptions={FORWARD_PASS}
      onFocus={onFocus}
      includeInputInList
      value={option}
      disabled={disabled}
      noOptionsText={t("common:noResults")}
      onChange={onOptionSelected}
      onInputChange={onInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          variant="filled"
          fullWidth
          autoComplete="new-password"
          slotProps={{
            input: {
              ...params.InputProps,
              disableUnderline: true,
            }
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.key}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid item sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                <Typography>
                  {option.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {option.address || option.formattedPlace}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />)
  );
}


